import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, differenceInDays, parseISO, startOfMonth, endOfMonth, subMonths, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Format date from YYYY-MM-DD to DD/MM/YYYY for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const year = parts[0].slice(-2); // Only last 2 digits
  return `${parts[2]}/${parts[1]}/${year}`;
};
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, ComposedChart
} from 'recharts';
import {
  Wallet, Plus, Search, Filter, Calendar, AlertTriangle, CheckCircle, Clock,
  Eye, Edit2, Trash2, CreditCard, Building, Receipt, TrendingUp, TrendingDown,
  RefreshCw, Pause, Play, FileSpreadsheet, X, ChevronRight, DollarSign, Loader2,
  ChevronDown, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_TYPES = [
  { value: 'GASTO_FIJO', label: 'Gasto Fijo', color: 'bg-blue-100 text-blue-700' },
  { value: 'PAGO_PROVEEDORES', label: 'Pago a Proveedores', color: 'bg-purple-100 text-purple-700' },
  { value: 'GASTO_VARIABLE', label: 'Gasto Variable', color: 'bg-slate-100 text-slate-700' },
];

const PAYMENT_METHODS = ['Efectivo', 'Tarjeta Crédito/Débito', 'Transferencia', 'Cheque'];
const RECURRENCE_FREQUENCIES = ['Semanal', 'Quincenal', 'Mensual'];

const PAYMENT_STATUS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-red-100 text-red-700' },
  PAGO_PARCIAL: { label: 'Pago Parcial', color: 'bg-yellow-100 text-yellow-700' },
  PAGADO: { label: 'Pagado', color: 'bg-green-100 text-green-700' },
  VENCIDO: { label: 'Vencido', color: 'bg-red-200 text-red-800' },
};

const initialExpenseForm = {
  name: '',
  expense_type: '',
  total_amount: '',
  entry_date: format(new Date(), 'yyyy-MM-dd'),
  due_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
  invoice_number: '',
  supplier: '',
  notes: '',
  is_recurring: false,
  recurrence_frequency: '',
  recurrence_start_date: '',
  recurrence_end_date: '',
};

const initialPaymentForm = {
  amount: '',
  payment_date: format(new Date(), 'yyyy-MM-dd'),
  payment_method: 'Efectivo',
  reference_number: '',
  notes: '',
};

const Expenses = () => {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Modals
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [expenseForm, setExpenseForm] = useState(initialExpenseForm);
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [hidePaid, setHidePaid] = useState(true); // Ocultar gastos pagados por defecto
  
  // Report state
  const [reportData, setReportData] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('this_month');
  const [reportLoading, setReportLoading] = useState(false);
  
  // Expenses Report state (new)
  const [expensesReportData, setExpensesReportData] = useState(null);
  const [expensesReportLoading, setExpensesReportLoading] = useState(false);
  const [expensesReportStartDate, setExpensesReportStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [expensesReportEndDate, setExpensesReportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Tab counts
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    due30: 0,
    due60: 0,
    overdue: 0
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchExpenses();
    fetchTabCounts();
  }, []);

  useEffect(() => {
    if (activeTab === 'report') {
      fetchReport();
    }
  }, [activeTab, reportPeriod]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/expenses`, { headers });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabCounts = async () => {
    try {
      const [due30, due60, overdue] = await Promise.all([
        axios.get(`${API_URL}/api/expenses/due-soon?days=30`, { headers }),
        axios.get(`${API_URL}/api/expenses/due-soon?days=60`, { headers }),
        axios.get(`${API_URL}/api/expenses/overdue`, { headers })
      ]);
      
      setTabCounts({
        all: expenses.length,
        due30: due30.data.count,
        due60: due60.data.count,
        overdue: overdue.data.count
      });
    } catch (error) {
      console.error('Error fetching tab counts:', error);
    }
  };

  const getReportDates = () => {
    const today = new Date();
    switch (reportPeriod) {
      case 'this_month':
        return {
          start: format(startOfMonth(today), 'yyyy-MM-dd'),
          end: format(endOfMonth(today), 'yyyy-MM-dd')
        };
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        return {
          start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          end: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
        };
      case 'last_3_months':
        return {
          start: format(startOfMonth(subMonths(today, 2)), 'yyyy-MM-dd'),
          end: format(endOfMonth(today), 'yyyy-MM-dd')
        };
      case 'last_6_months':
        return {
          start: format(startOfMonth(subMonths(today, 5)), 'yyyy-MM-dd'),
          end: format(endOfMonth(today), 'yyyy-MM-dd')
        };
      case 'this_year':
        return {
          start: `${today.getFullYear()}-01-01`,
          end: format(endOfMonth(today), 'yyyy-MM-dd')
        };
      default:
        return {
          start: format(startOfMonth(today), 'yyyy-MM-dd'),
          end: format(endOfMonth(today), 'yyyy-MM-dd')
        };
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const dates = getReportDates();
      const response = await axios.get(
        `${API_URL}/api/finance/report?start_date=${dates.start}&end_date=${dates.end}`,
        { headers }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error al cargar reporte');
    } finally {
      setReportLoading(false);
    }
  };

  const filteredExpenses = useMemo(() => {
    return expenses.filter((expense) => {
      // Ocultar gastos pagados si hidePaid está activo
      if (hidePaid && expense.payment_status === 'PAGADO') {
        return false;
      }
      
      const matchesSearch = !searchTerm || 
        expense.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (expense.supplier && expense.supplier.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesType = filterType === 'all' || expense.expense_type === filterType;
      
      let matchesStatus = true;
      if (filterStatus !== 'all') {
        matchesStatus = expense.payment_status === filterStatus;
      }
      
      let matchesDate = true;
      if (filterDateStart) {
        matchesDate = matchesDate && expense.entry_date >= filterDateStart;
      }
      if (filterDateEnd) {
        matchesDate = matchesDate && expense.entry_date <= filterDateEnd;
      }
      
      return matchesSearch && matchesType && matchesStatus && matchesDate;
    });
  }, [expenses, searchTerm, filterType, filterStatus, filterDateStart, filterDateEnd, hidePaid]);

  const getTabExpenses = () => {
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    
    switch (activeTab) {
      case 'due30':
        const date30 = format(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
        return filteredExpenses.filter(e => 
          e.due_date >= todayStr && e.due_date <= date30 && e.payment_status !== 'PAGADO'
        );
      case 'due60':
        const date60 = format(new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
        return filteredExpenses.filter(e => 
          e.due_date >= todayStr && e.due_date <= date60 && e.payment_status !== 'PAGADO'
        );
      case 'overdue':
        return filteredExpenses.filter(e => 
          e.due_date < todayStr && e.payment_status !== 'PAGADO'
        );
      default:
        return filteredExpenses;
    }
  };

  const displayedExpenses = getTabExpenses();
  const tabTotalPending = displayedExpenses.reduce((sum, e) => sum + (e.pending_balance || 0), 0);

  const openCreateModal = () => {
    setEditingExpense(null);
    setExpenseForm(initialExpenseForm);
    setIsExpenseModalOpen(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      name: expense.name,
      expense_type: expense.expense_type,
      total_amount: expense.total_amount.toString(),
      entry_date: expense.entry_date,
      due_date: expense.due_date,
      invoice_number: expense.invoice_number || '',
      supplier: expense.supplier || '',
      notes: expense.notes || '',
      is_recurring: expense.is_recurring || false,
      recurrence_frequency: expense.recurrence_frequency || '',
      recurrence_start_date: expense.recurrence_start_date || '',
      recurrence_end_date: expense.recurrence_end_date || '',
    });
    setIsExpenseModalOpen(true);
  };

  const openDetailModal = (expense) => {
    setSelectedExpense(expense);
    setIsDetailModalOpen(true);
  };

  const openPaymentModal = (expense) => {
    setSelectedExpense(expense);
    setPaymentForm({
      ...initialPaymentForm,
      amount: expense.pending_balance.toString()
    });
    setIsPaymentModalOpen(true);
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    
    if (!expenseForm.name || !expenseForm.expense_type || !expenseForm.total_amount || !expenseForm.due_date) {
      toast.error('Complete los campos requeridos');
      return;
    }

    const payload = {
      ...expenseForm,
      total_amount: parseFloat(expenseForm.total_amount) || 0,
    };

    setSaving(true);
    try {
      if (editingExpense) {
        await axios.put(`${API_URL}/api/expenses/${editingExpense.id}`, payload, { headers });
        toast.success('Gasto actualizado');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload, { headers });
        toast.success('Gasto registrado');
      }
      setIsExpenseModalOpen(false);
      fetchExpenses();
      fetchTabCounts();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    const payload = {
      ...paymentForm,
      amount: parseFloat(paymentForm.amount) || 0,
    };

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/expenses/${selectedExpense.id}/payments`, payload, { headers });
      toast.success('Pago registrado');
      setIsPaymentModalOpen(false);
      setIsDetailModalOpen(false);
      fetchExpenses();
      fetchTabCounts();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error('Error al registrar pago');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/expenses/${expenseToDelete.id}`, { headers });
      toast.success('Gasto eliminado');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
      fetchTabCounts();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Error al eliminar');
    }
  };

  const toggleRecurrence = async (expense) => {
    try {
      await axios.put(`${API_URL}/api/expenses/${expense.id}/recurrence/toggle`, {}, { headers });
      toast.success(expense.recurrence_active ? 'Recurrencia pausada' : 'Recurrencia activada');
      fetchExpenses();
    } catch (error) {
      console.error('Error toggling recurrence:', error);
      toast.error('Error al cambiar recurrencia');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
    }).format(amount);
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = parseISO(dueDate);
    return differenceInDays(due, today);
  };

  const getTypeInfo = (type) => {
    return EXPENSE_TYPES.find(t => t.value === type) || EXPENSE_TYPES[2];
  };

  const getStatusInfo = (status) => {
    return PAYMENT_STATUS[status] || PAYMENT_STATUS.PENDIENTE;
  };

  const exportReportToExcel = async () => {
    if (!reportData) return;
    
    const dates = getReportDates();
    
    try {
      // Fetch detailed data
      const [incomeDetails, expenseDetails] = await Promise.all([
        axios.get(`${API_URL}/api/finance/income-details?start_date=${dates.start}&end_date=${dates.end}`, { headers }),
        axios.get(`${API_URL}/api/finance/expense-details?start_date=${dates.start}&end_date=${dates.end}`, { headers })
      ]);

      const workbook = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryData = [
        ['REPORTE FINANCIERO - OFTALMODIAGNOSIS'],
        [''],
        ['Período:', `${formatDateDisplay(dates.start)} al ${formatDateDisplay(dates.end)}`],
        [''],
        ['RESUMEN GENERAL'],
        ['Total Ingresos:', formatCurrency(reportData.summary.total_income)],
        ['Total Gastos:', formatCurrency(reportData.summary.total_expenses)],
        ['Balance:', formatCurrency(reportData.summary.balance)],
        [''],
        ['DETALLE MENSUAL'],
        ['Mes', 'Ingresos', 'Gastos Fijos', 'Gastos Proveedores', 'Gastos Variables', 'Total Gastos', 'Balance'],
        ...reportData.monthly_data.map(m => [
          m.month_name, 
          formatCurrency(m.income), 
          formatCurrency(m.expenses_fixed || 0), 
          formatCurrency(m.expenses_supplier || 0), 
          formatCurrency(m.expenses_variable || 0), 
          formatCurrency(m.expenses_total), 
          formatCurrency(m.balance)
        ]),
        [''],
        ['GASTOS POR TIPO'],
        ['Tipo', 'Monto'],
        ['Gastos Fijos', formatCurrency(reportData.expenses_by_type?.GASTO_FIJO || 0)],
        ['Pago a Proveedores', formatCurrency(reportData.expenses_by_type?.PAGO_PROVEEDORES || 0)],
        ['Gastos Variables', formatCurrency(reportData.expenses_by_type?.GASTO_VARIABLE || 0)],
        ['TOTAL GASTOS', formatCurrency(reportData.summary.total_expenses)]
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

      // Sheet 2: Income Details (Desglose de Ingresos)
      const incomeRows = [
        ['DESGLOSE DE INGRESOS'],
        [''],
        ['Fecha', 'Paciente', 'Tipo de Examen', 'Forma de Pago', 'Monto']
      ];
      
      if (incomeDetails.data && incomeDetails.data.length > 0) {
        incomeDetails.data.forEach(item => {
          incomeRows.push([
            formatDateDisplay(item.fecha || item.date),
            item.paciente || item.nombre || item.patient_name || '-',
            item.tipo_examen || item.exam_type || '-',
            item.forma_pago || item.payment_method || '-',
            formatCurrency(item.monto || item.total || item.amount || 0)
          ]);
        });
        
        // Total row
        const totalIncome = incomeDetails.data.reduce((sum, item) => 
          sum + (item.monto || item.total || item.amount || 0), 0);
        incomeRows.push(['']);
        incomeRows.push(['', '', '', 'TOTAL INGRESOS:', formatCurrency(totalIncome)]);
      } else {
        incomeRows.push(['No hay ingresos en este período']);
      }
      
      const incomeSheet = XLSX.utils.aoa_to_sheet(incomeRows);
      XLSX.utils.book_append_sheet(workbook, incomeSheet, 'Desglose Ingresos');

      // Sheet 3: Expense Details (Desglose de Egresos)
      const expenseRows = [
        ['DESGLOSE DE EGRESOS'],
        [''],
        ['Fecha', 'Nombre', 'Tipo', 'Proveedor', 'Monto Total', 'Pagado', 'Pendiente', 'Estado']
      ];
      
      if (expenseDetails.data && expenseDetails.data.length > 0) {
        expenseDetails.data.forEach(item => {
          const typeLabel = EXPENSE_TYPES.find(t => t.value === item.expense_type)?.label || item.expense_type;
          const statusLabel = PAYMENT_STATUS[item.payment_status]?.label || item.payment_status;
          
          expenseRows.push([
            formatDateDisplay(item.entry_date || item.fecha || item.date),
            item.name || item.nombre || '-',
            typeLabel,
            item.supplier || item.proveedor || '-',
            formatCurrency(item.total_amount || item.monto || 0),
            formatCurrency(item.total_paid || 0),
            formatCurrency(item.pending_balance || 0),
            statusLabel
          ]);
        });
        
        // Total row
        const totalExpenses = expenseDetails.data.reduce((sum, item) => 
          sum + (item.total_amount || item.monto || 0), 0);
        const totalPaid = expenseDetails.data.reduce((sum, item) => 
          sum + (item.total_paid || 0), 0);
        const totalPending = expenseDetails.data.reduce((sum, item) => 
          sum + (item.pending_balance || 0), 0);
        
        expenseRows.push(['']);
        expenseRows.push(['', '', '', 'TOTALES:', 
          formatCurrency(totalExpenses), 
          formatCurrency(totalPaid), 
          formatCurrency(totalPending), 
          ''
        ]);
      } else {
        expenseRows.push(['No hay egresos en este período']);
      }
      
      const expenseSheet = XLSX.utils.aoa_to_sheet(expenseRows);
      XLSX.utils.book_append_sheet(workbook, expenseSheet, 'Desglose Egresos');

      const filename = `Reporte_Financiero_Oftalmodiagnosis_${dates.start}_${dates.end}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success('Reporte exportado a Excel');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Error al exportar reporte');
    }
  };

  const exportReportToPDF = async () => {
    if (!reportData) return;
    
    const dates = getReportDates();
    
    try {
      // Fetch detailed data for the PDF
      const [incomeDetails, expenseDetails] = await Promise.all([
        axios.get(`${API_URL}/api/finance/income-details?start_date=${dates.start}&end_date=${dates.end}`, { headers }),
        axios.get(`${API_URL}/api/finance/expense-details?start_date=${dates.start}&end_date=${dates.end}`, { headers })
      ]);

      const doc = new jsPDF('portrait');
      
      // Title
      doc.setFontSize(18);
      doc.text('Reporte Financiero - Oftalmodiagnosis', 14, 20);
      
      doc.setFontSize(10);
      doc.text(`Período: ${formatDateDisplay(dates.start)} al ${formatDateDisplay(dates.end)}`, 14, 28);
      
      // Summary section
      doc.setFontSize(14);
      doc.text('Resumen General', 14, 40);
      
      doc.setFontSize(10);
      doc.text(`Total Ingresos: ${formatCurrency(reportData.summary.total_income)}`, 14, 50);
      doc.text(`Total Gastos: ${formatCurrency(reportData.summary.total_expenses)}`, 14, 58);
      doc.text(`Balance: ${formatCurrency(reportData.summary.balance)}`, 14, 66);
      
      // Monthly data table
      if (reportData.monthly_data && reportData.monthly_data.length > 0) {
        doc.setFontSize(14);
        doc.text('Detalle Mensual', 14, 80);
        
        const monthlyTableData = reportData.monthly_data.map(m => [
          m.month_name,
          formatCurrency(m.income),
          formatCurrency(m.expenses_fixed || 0),
          formatCurrency(m.expenses_supplier || 0),
          formatCurrency(m.expenses_variable || 0),
          formatCurrency(m.expenses_total),
          formatCurrency(m.balance)
        ]);
        
        autoTable(doc, {
          startY: 85,
          head: [['Mes', 'Ingresos', 'G. Fijos', 'G. Prov.', 'G. Var.', 'Total Gastos', 'Balance']],
          body: monthlyTableData,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [26, 81, 146] }
        });
      }
      
      // Expenses by type breakdown
      if (reportData.expenses_by_type) {
        const finalY = doc.lastAutoTable?.finalY || 120;
        
        doc.setFontSize(14);
        doc.text('Gastos por Tipo', 14, finalY + 15);
        
        const expensesByTypeData = [];
        if (reportData.expenses_by_type.GASTO_FIJO) {
          expensesByTypeData.push(['Gastos Fijos', formatCurrency(reportData.expenses_by_type.GASTO_FIJO)]);
        }
        if (reportData.expenses_by_type.PAGO_PROVEEDORES) {
          expensesByTypeData.push(['Pago a Proveedores', formatCurrency(reportData.expenses_by_type.PAGO_PROVEEDORES)]);
        }
        if (reportData.expenses_by_type.GASTO_VARIABLE) {
          expensesByTypeData.push(['Gastos Variables', formatCurrency(reportData.expenses_by_type.GASTO_VARIABLE)]);
        }
        
        if (expensesByTypeData.length > 0) {
          autoTable(doc, {
            startY: finalY + 20,
            head: [['Tipo de Gasto', 'Monto']],
            body: expensesByTypeData,
            foot: [['TOTAL', formatCurrency(reportData.summary.total_expenses)]],
            styles: { fontSize: 9 },
            headStyles: { fillColor: [220, 53, 69] },
            footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
          });
        }
      }

      // PAGE 2: Income Details (Desglose de Ingresos)
      doc.addPage();
      doc.setFontSize(16);
      doc.text('Desglose de Ingresos', 14, 20);
      doc.setFontSize(10);
      doc.text(`Período: ${formatDateDisplay(dates.start)} al ${formatDateDisplay(dates.end)}`, 14, 28);
      
      if (incomeDetails.data && incomeDetails.data.length > 0) {
        const incomeTableData = incomeDetails.data.map(item => [
          formatDateDisplay(item.fecha || item.date),
          (item.paciente || item.nombre || item.patient_name || '-').substring(0, 25),
          (item.tipo_examen || item.exam_type || '-').substring(0, 20),
          item.forma_pago || item.payment_method || '-',
          formatCurrency(item.monto || item.total || item.amount || 0)
        ]);
        
        const totalIncome = incomeDetails.data.reduce((sum, item) => 
          sum + (item.monto || item.total || item.amount || 0), 0);
        
        autoTable(doc, {
          startY: 35,
          head: [['Fecha', 'Paciente', 'Tipo Examen', 'Forma Pago', 'Monto']],
          body: incomeTableData,
          foot: [['', '', '', 'TOTAL:', formatCurrency(totalIncome)]],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [40, 167, 69] },
          footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
        });
      } else {
        doc.text('No hay ingresos registrados en este período.', 14, 40);
      }

      // PAGE 3: Expense Details (Desglose de Egresos)
      doc.addPage();
      doc.setFontSize(16);
      doc.text('Desglose de Egresos', 14, 20);
      doc.setFontSize(10);
      doc.text(`Período: ${formatDateDisplay(dates.start)} al ${formatDateDisplay(dates.end)}`, 14, 28);
      
      if (expenseDetails.data && expenseDetails.data.length > 0) {
        const expenseTableData = expenseDetails.data.map(item => {
          const typeLabel = EXPENSE_TYPES.find(t => t.value === item.expense_type)?.label || item.expense_type || '-';
          const statusLabel = PAYMENT_STATUS[item.payment_status]?.label || item.payment_status || '-';
          
          return [
            formatDateDisplay(item.entry_date || item.fecha || item.date),
            (item.name || item.nombre || '-').substring(0, 20),
            typeLabel.substring(0, 15),
            (item.supplier || item.proveedor || '-').substring(0, 15),
            formatCurrency(item.total_amount || item.monto || 0),
            formatCurrency(item.total_paid || 0),
            formatCurrency(item.pending_balance || 0),
            statusLabel.substring(0, 10)
          ];
        });
        
        const totalExpenses = expenseDetails.data.reduce((sum, item) => 
          sum + (item.total_amount || item.monto || 0), 0);
        const totalPaid = expenseDetails.data.reduce((sum, item) => 
          sum + (item.total_paid || 0), 0);
        const totalPending = expenseDetails.data.reduce((sum, item) => 
          sum + (item.pending_balance || 0), 0);
        
        autoTable(doc, {
          startY: 35,
          head: [['Fecha', 'Nombre', 'Tipo', 'Proveedor', 'Total', 'Pagado', 'Pendiente', 'Estado']],
          body: expenseTableData,
          foot: [['', '', '', 'TOTALES:', 
            formatCurrency(totalExpenses), 
            formatCurrency(totalPaid), 
            formatCurrency(totalPending), 
            ''
          ]],
          styles: { fontSize: 7 },
          headStyles: { fillColor: [220, 53, 69] },
          footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
        });
      } else {
        doc.text('No hay egresos registrados en este período.', 14, 40);
      }
      
      const filename = `Reporte_Financiero_Oftalmodiagnosis_${dates.start}_${dates.end}.pdf`;
      doc.save(filename);
      toast.success('Reporte exportado a PDF');
    } catch (error) {
      console.error('Error exporting report to PDF:', error);
      toast.error('Error al exportar reporte');
    }
  };

  // Fetch expenses report with custom dates
  const fetchExpensesReport = async () => {
    setExpensesReportLoading(true);
    try {
      // Fetch all expenses and filter by entry_date
      const response = await axios.get(`${API_URL}/api/expenses`, { headers });
      
      const allExpenses = response.data || [];
      
      // Filter expenses by entry_date within the date range
      const filteredExpenses = allExpenses.filter(expense => {
        const entryDate = expense.entry_date;
        return entryDate >= expensesReportStartDate && entryDate <= expensesReportEndDate;
      });
      
      // Group by type
      const byType = {};
      EXPENSE_TYPES.forEach(t => {
        byType[t.value] = { label: t.label, total: 0, paid: 0, pending: 0, count: 0, items: [] };
      });
      
      filteredExpenses.forEach(expense => {
        const type = expense.expense_type || 'GASTO_VARIABLE';
        if (byType[type]) {
          byType[type].total += expense.total_amount || 0;
          byType[type].paid += expense.total_paid || 0;
          byType[type].pending += expense.pending_balance || 0;
          byType[type].count++;
          byType[type].items.push(expense);
        }
      });
      
      const totals = {
        total: filteredExpenses.reduce((sum, e) => sum + (e.total_amount || 0), 0),
        paid: filteredExpenses.reduce((sum, e) => sum + (e.total_paid || 0), 0),
        pending: filteredExpenses.reduce((sum, e) => sum + (e.pending_balance || 0), 0),
        count: filteredExpenses.length
      };
      
      setExpensesReportData({ expenses: filteredExpenses, byType, totals });
    } catch (error) {
      console.error('Error fetching expenses report:', error);
      toast.error('Error al cargar el reporte de gastos');
    } finally {
      setExpensesReportLoading(false);
    }
  };

  // Export expenses report to Excel
  const exportExpensesReportToExcel = () => {
    if (!expensesReportData || !expensesReportData.expenses.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    const rows = [];
    rows.push(['REPORTE DE GASTOS - OFTALMODIAGNOSIS']);
    rows.push([`Período: ${formatDateDisplay(expensesReportStartDate)} al ${formatDateDisplay(expensesReportEndDate)}`]);
    rows.push([]);
    rows.push(['RESUMEN POR TIPO']);
    rows.push(['Tipo', 'Cantidad', 'Total', 'Pagado', 'Pendiente']);
    
    Object.entries(expensesReportData.byType).forEach(([key, data]) => {
      if (data.count > 0) {
        rows.push([data.label, data.count, formatCurrency(data.total), formatCurrency(data.paid), formatCurrency(data.pending)]);
      }
    });
    
    rows.push(['TOTALES', expensesReportData.totals.count, formatCurrency(expensesReportData.totals.total), 
               formatCurrency(expensesReportData.totals.paid), formatCurrency(expensesReportData.totals.pending)]);
    rows.push([]);
    rows.push(['DETALLE DE GASTOS']);
    rows.push(['Fecha', 'Nombre', 'Tipo', 'Proveedor', 'Total', 'Pagado', 'Pendiente', 'Estado']);
    
    expensesReportData.expenses.forEach(expense => {
      const typeLabel = EXPENSE_TYPES.find(t => t.value === expense.expense_type)?.label || expense.expense_type;
      const statusLabel = PAYMENT_STATUS[expense.payment_status]?.label || expense.payment_status;
      rows.push([
        formatDateDisplay(expense.entry_date),
        expense.name,
        typeLabel,
        expense.supplier || '-',
        formatCurrency(expense.total_amount),
        formatCurrency(expense.total_paid || 0),
        formatCurrency(expense.pending_balance || 0),
        statusLabel
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte Gastos');
    XLSX.writeFile(wb, `Reporte_Gastos_${expensesReportStartDate}_${expensesReportEndDate}.xlsx`);
    toast.success('Reporte exportado a Excel');
  };

  // Export expenses report to PDF
  const exportExpensesReportToPDF = () => {
    if (!expensesReportData || !expensesReportData.expenses.length) {
      toast.error('No hay datos para exportar');
      return;
    }

    const doc = new jsPDF('landscape');
    
    doc.setFontSize(18);
    doc.text('Reporte de Gastos - Oftalmodiagnosis', 14, 20);
    doc.setFontSize(10);
    doc.text(`Período: ${formatDateDisplay(expensesReportStartDate)} al ${formatDateDisplay(expensesReportEndDate)}`, 14, 28);
    
    // Summary by type
    doc.setFontSize(14);
    doc.text('Resumen por Tipo', 14, 40);
    
    const summaryData = Object.entries(expensesReportData.byType)
      .filter(([_, data]) => data.count > 0)
      .map(([_, data]) => [data.label, data.count, formatCurrency(data.total), formatCurrency(data.paid), formatCurrency(data.pending)]);
    
    autoTable(doc, {
      startY: 45,
      head: [['Tipo', 'Cantidad', 'Total', 'Pagado', 'Pendiente']],
      body: summaryData,
      foot: [['TOTALES', expensesReportData.totals.count, formatCurrency(expensesReportData.totals.total), 
              formatCurrency(expensesReportData.totals.paid), formatCurrency(expensesReportData.totals.pending)]],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [26, 81, 146] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });
    
    // Detail table
    const detailY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.text('Detalle de Gastos', 14, detailY);
    
    const detailData = expensesReportData.expenses.map(expense => {
      const typeLabel = EXPENSE_TYPES.find(t => t.value === expense.expense_type)?.label || expense.expense_type;
      const statusLabel = PAYMENT_STATUS[expense.payment_status]?.label || expense.payment_status;
      return [
        formatDateDisplay(expense.entry_date),
        expense.name?.substring(0, 25) || '-',
        typeLabel,
        (expense.supplier || '-').substring(0, 15),
        formatCurrency(expense.total_amount),
        formatCurrency(expense.total_paid || 0),
        formatCurrency(expense.pending_balance || 0),
        statusLabel
      ];
    });
    
    autoTable(doc, {
      startY: detailY + 5,
      head: [['Fecha', 'Nombre', 'Tipo', 'Proveedor', 'Total', 'Pagado', 'Pendiente', 'Estado']],
      body: detailData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [220, 53, 69] }
    });
    
    doc.save(`Reporte_Gastos_${expensesReportStartDate}_${expensesReportEndDate}.pdf`);
    toast.success('Reporte exportado a PDF');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterStatus('all');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  const hasActiveFilters = searchTerm || filterType !== 'all' || filterStatus !== 'all' || filterDateStart || filterDateEnd;

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-[#1a5192]" />
            Gastos y Finanzas
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Gestiona los gastos y visualiza el estado financiero
          </p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold shadow-lg"
          data-testid="new-expense-button"
        >
          <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Nuevo Gasto
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 bg-white border">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1a5192] data-[state=active]:text-white">
            Todos los Gastos
          </TabsTrigger>
          <TabsTrigger value="due30" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white relative">
            Por Vencer 30d
            {tabCounts.due30 > 0 && (
              <Badge className="ml-2 bg-yellow-600 text-white text-xs">{tabCounts.due30}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="due60" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
            Por Vencer 60d
            {tabCounts.due60 > 0 && (
              <Badge className="ml-2 bg-orange-600 text-white text-xs">{tabCounts.due60}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="overdue" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            Vencidos
            {tabCounts.overdue > 0 && (
              <Badge className="ml-2 bg-red-600 text-white text-xs">{tabCounts.overdue}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="expenses_report" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
            Reporte Gastos
          </TabsTrigger>
          <TabsTrigger value="report" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            Reporte Financiero
          </TabsTrigger>
        </TabsList>

        {/* Tab Content: Expenses List */}
        <TabsContent value="all" className="mt-4">
          <ExpensesListContent 
            expenses={displayedExpenses}
            loading={loading}
            tabTotalPending={tabTotalPending}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterType={filterType}
            setFilterType={setFilterType}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterDateStart={filterDateStart}
            setFilterDateStart={setFilterDateStart}
            filterDateEnd={filterDateEnd}
            setFilterDateEnd={setFilterDateEnd}
            hidePaid={hidePaid}
            setHidePaid={setHidePaid}
            hasActiveFilters={hasActiveFilters}
            clearFilters={clearFilters}
            openDetailModal={openDetailModal}
            openEditModal={openEditModal}
            openPaymentModal={openPaymentModal}
            setExpenseToDelete={setExpenseToDelete}
            setDeleteDialogOpen={setDeleteDialogOpen}
            toggleRecurrence={toggleRecurrence}
            formatCurrency={formatCurrency}
            getDaysUntilDue={getDaysUntilDue}
            getTypeInfo={getTypeInfo}
            getStatusInfo={getStatusInfo}
            showFilters={true}
          />
        </TabsContent>

        <TabsContent value="due30" className="mt-4">
          <ExpensesListContent 
            expenses={displayedExpenses}
            loading={loading}
            tabTotalPending={tabTotalPending}
            tabTitle="Pagos por vencer en los próximos 30 días"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            openDetailModal={openDetailModal}
            openEditModal={openEditModal}
            openPaymentModal={openPaymentModal}
            setExpenseToDelete={setExpenseToDelete}
            setDeleteDialogOpen={setDeleteDialogOpen}
            toggleRecurrence={toggleRecurrence}
            formatCurrency={formatCurrency}
            getDaysUntilDue={getDaysUntilDue}
            getTypeInfo={getTypeInfo}
            getStatusInfo={getStatusInfo}
            showFilters={false}
          />
        </TabsContent>

        <TabsContent value="due60" className="mt-4">
          <ExpensesListContent 
            expenses={displayedExpenses}
            loading={loading}
            tabTotalPending={tabTotalPending}
            tabTitle="Pagos por vencer en los próximos 60 días"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            openDetailModal={openDetailModal}
            openEditModal={openEditModal}
            openPaymentModal={openPaymentModal}
            setExpenseToDelete={setExpenseToDelete}
            setDeleteDialogOpen={setDeleteDialogOpen}
            toggleRecurrence={toggleRecurrence}
            formatCurrency={formatCurrency}
            getDaysUntilDue={getDaysUntilDue}
            getTypeInfo={getTypeInfo}
            getStatusInfo={getStatusInfo}
            showFilters={false}
          />
        </TabsContent>

        <TabsContent value="overdue" className="mt-4">
          <ExpensesListContent 
            expenses={displayedExpenses}
            loading={loading}
            tabTotalPending={tabTotalPending}
            tabTitle="Pagos vencidos"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            openDetailModal={openDetailModal}
            openEditModal={openEditModal}
            openPaymentModal={openPaymentModal}
            setExpenseToDelete={setExpenseToDelete}
            setDeleteDialogOpen={setDeleteDialogOpen}
            toggleRecurrence={toggleRecurrence}
            formatCurrency={formatCurrency}
            getDaysUntilDue={getDaysUntilDue}
            getTypeInfo={getTypeInfo}
            getStatusInfo={getStatusInfo}
            showFilters={false}
          />
        </TabsContent>

        {/* Tab Content: Expenses Report */}
        <TabsContent value="expenses_report" className="mt-4">
          <Card className="bg-white border-slate-100 shadow-soft">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <Label className="text-slate-600">Desde:</Label>
                  <Input
                    type="date"
                    value={expensesReportStartDate}
                    onChange={(e) => setExpensesReportStartDate(e.target.value)}
                    className="w-40 bg-slate-50"
                  />
                  <Label className="text-slate-600">Hasta:</Label>
                  <Input
                    type="date"
                    value={expensesReportEndDate}
                    onChange={(e) => setExpensesReportEndDate(e.target.value)}
                    className="w-40 bg-slate-50"
                  />
                  <Button
                    onClick={fetchExpensesReport}
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={expensesReportLoading}
                  >
                    {expensesReportLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cargando...</>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Generar Reporte
                      </>
                    )}
                  </Button>
                </div>
                
                {expensesReportData && expensesReportData.expenses.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50">
                        <FileSpreadsheet className="w-5 h-5 mr-2" />
                        Exportar
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={exportExpensesReportToExcel} className="cursor-pointer">
                        <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                        Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={exportExpensesReportToPDF} className="cursor-pointer">
                        <FileText className="w-4 h-4 mr-2 text-red-600" />
                        PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {expensesReportLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : expensesReportData ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-slate-50 border-slate-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-slate-700">{expensesReportData.totals.count}</p>
                        <p className="text-sm text-slate-500">Total Gastos</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(expensesReportData.totals.total)}</p>
                        <p className="text-sm text-red-500">Monto Total</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-emerald-700">{formatCurrency(expensesReportData.totals.paid)}</p>
                        <p className="text-sm text-emerald-500">Pagado</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-amber-700">{formatCurrency(expensesReportData.totals.pending)}</p>
                        <p className="text-sm text-amber-500">Pendiente</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Summary by Type */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(expensesReportData.byType).map(([key, data]) => (
                      data.count > 0 && (
                        <Card key={key} className="bg-white border-slate-200">
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-slate-700 mb-2">{data.label}</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Cantidad:</span>
                                <span className="font-medium">{data.count}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Total:</span>
                                <span className="font-medium text-red-600">{formatCurrency(data.total)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Pagado:</span>
                                <span className="font-medium text-emerald-600">{formatCurrency(data.paid)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Pendiente:</span>
                                <span className="font-medium text-amber-600">{formatCurrency(data.pending)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )
                    ))}
                  </div>

                  {/* Detail Table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100">
                          <TableHead className="font-semibold">Fecha</TableHead>
                          <TableHead className="font-semibold">Nombre</TableHead>
                          <TableHead className="font-semibold">Tipo</TableHead>
                          <TableHead className="font-semibold">Proveedor</TableHead>
                          <TableHead className="font-semibold text-right">Total</TableHead>
                          <TableHead className="font-semibold text-right">Pagado</TableHead>
                          <TableHead className="font-semibold text-right">Pendiente</TableHead>
                          <TableHead className="font-semibold text-center">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expensesReportData.expenses.map((expense, idx) => {
                          const typeInfo = EXPENSE_TYPES.find(t => t.value === expense.expense_type);
                          const statusInfo = PAYMENT_STATUS[expense.payment_status];
                          return (
                            <TableRow key={idx} className="hover:bg-slate-50">
                              <TableCell className="font-mono text-sm">{formatDateDisplay(expense.entry_date)}</TableCell>
                              <TableCell className="font-medium">{expense.name}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {typeInfo?.label || expense.expense_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-slate-600">{expense.supplier || '-'}</TableCell>
                              <TableCell className="text-right font-medium text-red-600">
                                {formatCurrency(expense.total_amount)}
                              </TableCell>
                              <TableCell className="text-right text-emerald-600">
                                {formatCurrency(expense.total_paid || 0)}
                              </TableCell>
                              <TableCell className="text-right text-amber-600">
                                {formatCurrency(expense.pending_balance || 0)}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className={statusInfo?.color || 'bg-slate-100'}>
                                  {statusInfo?.label || expense.payment_status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Selecciona un rango de fechas y haz clic en "Generar Reporte"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Content: Financial Report */}
        <TabsContent value="report" className="mt-4">
          <FinancialReportContent 
            reportData={reportData}
            reportPeriod={reportPeriod}
            setReportPeriod={setReportPeriod}
            reportLoading={reportLoading}
            exportReportToExcel={exportReportToExcel}
            exportReportToPDF={exportReportToPDF}
            formatCurrency={formatCurrency}
          />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Expense Modal */}
      <Dialog open={isExpenseModalOpen} onOpenChange={setIsExpenseModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expense-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#1a5192]" />
              {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveExpense} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="name">Nombre del Gasto *</Label>
                <Input
                  id="name"
                  value={expenseForm.name}
                  onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })}
                  placeholder="Ej: Alquiler local, Compra de insumos"
                  className="bg-slate-50"
                  data-testid="expense-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tipo de Gasto *</Label>
                <Select 
                  value={expenseForm.expense_type} 
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, expense_type: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="expense-type">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="total_amount">Monto Total (Q) *</Label>
                <Input
                  id="total_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={expenseForm.total_amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, total_amount: e.target.value })}
                  placeholder="0.00"
                  className="bg-slate-50"
                  data-testid="expense-amount"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="entry_date">Fecha Registro *</Label>
                <Input
                  id="entry_date"
                  type="date"
                  value={expenseForm.entry_date}
                  onChange={(e) => {
                    const newEntryDate = e.target.value;
                    const newDueDate = newEntryDate 
                      ? format(addDays(parseISO(newEntryDate), 30), 'yyyy-MM-dd')
                      : '';
                    setExpenseForm({ 
                      ...expenseForm, 
                      entry_date: newEntryDate,
                      due_date: newDueDate
                    });
                  }}
                  className="bg-slate-50"
                  data-testid="expense-entry-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="due_date">Fecha Vencimiento *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={expenseForm.due_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, due_date: e.target.value })}
                  className="bg-slate-50"
                  data-testid="expense-due-date"
                />
                <p className="text-xs text-slate-500">Se calcula automáticamente a 30 días de la fecha de registro</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="invoice_number">Número de Factura</Label>
                <Input
                  id="invoice_number"
                  value={expenseForm.invoice_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, invoice_number: e.target.value })}
                  placeholder="FAC-001"
                  className="bg-slate-50"
                  data-testid="expense-invoice"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="supplier">Proveedor o Beneficiario</Label>
                <Input
                  id="supplier"
                  value={expenseForm.supplier}
                  onChange={(e) => setExpenseForm({ ...expenseForm, supplier: e.target.value })}
                  placeholder="A quién se le paga"
                  className="bg-slate-50"
                  data-testid="expense-supplier"
                />
              </div>
              
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="notes">Observaciones</Label>
                <Textarea
                  id="notes"
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                  placeholder="Notas adicionales..."
                  className="bg-slate-50 resize-none"
                  rows={2}
                  data-testid="expense-notes"
                />
              </div>
              
              {/* Recurring section */}
              <div className="sm:col-span-2 p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="is_recurring" className="font-medium">¿Es gasto recurrente?</Label>
                    <p className="text-sm text-slate-500">Se generará automáticamente según la frecuencia</p>
                  </div>
                  <Switch
                    id="is_recurring"
                    checked={expenseForm.is_recurring}
                    onCheckedChange={(checked) => setExpenseForm({ ...expenseForm, is_recurring: checked })}
                    data-testid="expense-recurring"
                  />
                </div>
                
                {expenseForm.is_recurring && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-200">
                    <div className="space-y-2">
                      <Label>Frecuencia</Label>
                      <Select 
                        value={expenseForm.recurrence_frequency} 
                        onValueChange={(value) => setExpenseForm({ ...expenseForm, recurrence_frequency: value })}
                      >
                        <SelectTrigger className="bg-white" data-testid="expense-frequency">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_FREQUENCIES.map((freq) => (
                            <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_start">Fecha Inicio</Label>
                      <Input
                        id="recurrence_start"
                        type="date"
                        value={expenseForm.recurrence_start_date}
                        onChange={(e) => setExpenseForm({ ...expenseForm, recurrence_start_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_end">Fecha Fin (opcional)</Label>
                      <Input
                        id="recurrence_end"
                        type="date"
                        value={expenseForm.recurrence_end_date}
                        onChange={(e) => setExpenseForm({ ...expenseForm, recurrence_end_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-2 pt-4 border-t border-slate-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsExpenseModalOpen(false)}
                className="border-slate-300 text-slate-700"
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold"
                data-testid="save-expense-button"
                disabled={saving}
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                ) : (
                  editingExpense ? 'Actualizar' : 'Guardar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Expense Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expense-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#1a5192]" />
              Detalle del Gasto
            </DialogTitle>
          </DialogHeader>
          
          {selectedExpense && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">{selectedExpense.name}</h3>
                  <Badge className={`${getStatusInfo(selectedExpense.payment_status).color} border-0`}>
                    {getStatusInfo(selectedExpense.payment_status).label}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Tipo</p>
                    <Badge className={`${getTypeInfo(selectedExpense.expense_type).color} border-0`}>
                      {getTypeInfo(selectedExpense.expense_type).label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Proveedor</p>
                    <p className="font-medium text-slate-900">{selectedExpense.supplier || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Fecha de Ingreso</p>
                    <p className="font-medium text-slate-900">{formatDateDisplay(selectedExpense.entry_date)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Fecha de Vencimiento</p>
                    <p className="font-medium text-slate-900">{formatDateDisplay(selectedExpense.due_date)}</p>
                  </div>
                  {selectedExpense.invoice_number && (
                    <div>
                      <p className="text-slate-500">No. Factura</p>
                      <p className="font-medium text-slate-900">{selectedExpense.invoice_number}</p>
                    </div>
                  )}
                  {selectedExpense.is_recurring && (
                    <div>
                      <p className="text-slate-500">Recurrencia</p>
                      <p className="font-medium text-slate-900 flex items-center gap-1">
                        <RefreshCw className="w-4 h-4" />
                        {selectedExpense.recurrence_frequency}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Progress */}
              <div className="bg-emerald-50 rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Estado de Pago
                </h4>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-emerald-600">Monto Total</p>
                    <p className="font-bold text-lg text-emerald-900">{formatCurrency(selectedExpense.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-600">Total Pagado</p>
                    <p className="font-bold text-lg text-emerald-700">{formatCurrency(selectedExpense.total_paid)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-red-600">Saldo Pendiente</p>
                    <p className="font-bold text-lg text-red-700">{formatCurrency(selectedExpense.pending_balance)}</p>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Progreso de pago</span>
                    <span className="font-medium">
                      {Math.round((selectedExpense.total_paid / selectedExpense.total_amount) * 100)}%
                    </span>
                  </div>
                  <Progress 
                    value={(selectedExpense.total_paid / selectedExpense.total_amount) * 100} 
                    className="h-3"
                  />
                </div>
              </div>

              {/* Payment History */}
              {selectedExpense.payments && selectedExpense.payments.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-800">Historial de Pagos</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Fecha</TableHead>
                          <TableHead>Monto</TableHead>
                          <TableHead>Medio</TableHead>
                          <TableHead>Referencia</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedExpense.payments.map((payment, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{formatDateDisplay(payment.payment_date)}</TableCell>
                            <TableCell className="font-medium">{formatCurrency(payment.amount)}</TableCell>
                            <TableCell>{payment.payment_method}</TableCell>
                            <TableCell className="text-slate-500">{payment.reference_number || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedExpense.pending_balance > 0 && (
                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={() => openPaymentModal(selectedExpense)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="register-payment-button"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Registrar Pago
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          
          {selectedExpense && (
            <form onSubmit={handleSavePayment} className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-500">Gasto</p>
                <p className="font-medium">{selectedExpense.name}</p>
                <p className="text-sm text-slate-600 mt-1">
                  Saldo pendiente: <span className="font-bold text-red-600">{formatCurrency(selectedExpense.pending_balance)}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payment_amount">Monto del Pago (Q) *</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedExpense.pending_balance}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="bg-slate-50"
                  data-testid="payment-amount"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payment_date">Fecha de Pago *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  className="bg-slate-50"
                  data-testid="payment-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Medio de Pago *</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                >
                  <SelectTrigger className="bg-slate-50" data-testid="payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="reference_number">No. Referencia / Comprobante</Label>
                <Input
                  id="reference_number"
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                  placeholder="Número de referencia"
                  className="bg-slate-50"
                  data-testid="payment-reference"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="payment_notes">Observaciones</Label>
                <Input
                  id="payment_notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Notas del pago"
                  className="bg-slate-50"
                />
              </div>
              
              <DialogFooter className="gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsPaymentModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-payment-button"
                  disabled={saving}
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                  ) : (
                    'Registrar Pago'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              {expenseToDelete && (
                <>
                  Está a punto de eliminar el gasto "<strong>{expenseToDelete.name}</strong>".
                  <br /><br />
                  Esta acción no se puede deshacer y se perderán todos los pagos asociados.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExpense}
              className="bg-red-500 hover:bg-red-600"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Sub-component for expenses list content
const ExpensesListContent = ({
  expenses, loading, tabTotalPending, tabTitle,
  searchTerm, setSearchTerm,
  filterType, setFilterType,
  filterStatus, setFilterStatus,
  filterDateStart, setFilterDateStart,
  filterDateEnd, setFilterDateEnd,
  hidePaid, setHidePaid,
  hasActiveFilters, clearFilters,
  openDetailModal, openEditModal, openPaymentModal,
  setExpenseToDelete, setDeleteDialogOpen,
  toggleRecurrence, formatCurrency, getDaysUntilDue,
  getTypeInfo, getStatusInfo, showFilters
}) => {
  return (
    <div className="space-y-4">
      {/* Summary for tabs */}
      {tabTitle && (
        <Card className="bg-white border-slate-100 shadow-soft">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">{tabTitle}</p>
                <p className="text-2xl font-bold text-slate-900">{expenses.length} gastos</p>
              </div>
              <div className="text-right">
                <p className="text-slate-500 text-sm">Total Pendiente</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(tabTotalPending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {showFilters && (
        <Card className="bg-white border-slate-100 shadow-soft">
          <CardContent className="p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o proveedor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-50 h-11"
                    data-testid="search-expenses"
                  />
                </div>
                
                {/* Switch to hide paid expenses */}
                {setHidePaid && (
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                    <input
                      type="checkbox"
                      id="hidePaid"
                      checked={hidePaid}
                      onChange={(e) => setHidePaid(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                    />
                    <Label htmlFor="hidePaid" className="text-sm text-slate-600 cursor-pointer whitespace-nowrap">
                      Ocultar pagados
                    </Label>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Tipo de Gasto</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {EXPENSE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Estado de Pago</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                      <SelectItem value="PAGO_PARCIAL">Pago Parcial</SelectItem>
                      <SelectItem value="PAGADO">Pagado</SelectItem>
                      <SelectItem value="VENCIDO">Vencido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1 min-w-[130px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Fecha Desde</Label>
                  <Input
                    type="date"
                    value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)}
                    className="bg-slate-50"
                  />
                </div>
                
                <div className="flex-1 min-w-[130px]">
                  <Label className="text-xs text-slate-500 mb-1 block">Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    className="bg-slate-50"
                  />
                </div>
                
                {hasActiveFilters && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={clearFilters}
                    className="border-slate-300 text-slate-600"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="bg-white border-slate-100 shadow-soft overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="expenses-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Nombre</TableHead>
                  <TableHead className="font-semibold text-slate-700">Tipo</TableHead>
                  <TableHead className="font-semibold text-slate-700">Monto Total</TableHead>
                  <TableHead className="font-semibold text-slate-700">Pagado</TableHead>
                  <TableHead className="font-semibold text-slate-700">Pendiente</TableHead>
                  <TableHead className="font-semibold text-slate-700">Vencimiento</TableHead>
                  <TableHead className="font-semibold text-slate-700">Estado</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Cargando...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                      No hay gastos registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((expense) => {
                    const daysUntilDue = getDaysUntilDue(expense.due_date);
                    const isOverdue = daysUntilDue < 0 && expense.payment_status !== 'PAGADO';
                    const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 7 && expense.payment_status !== 'PAGADO';
                    
                    return (
                      <TableRow 
                        key={expense.id} 
                        className={`hover:bg-slate-50 transition-colors ${
                          isOverdue ? 'bg-red-50/50 border-l-4 border-l-red-400' : 
                          isDueSoon ? 'bg-yellow-50/50 border-l-4 border-l-yellow-400' : ''
                        }`}
                        data-testid={`expense-row-${expense.id}`}
                      >
                        <TableCell>
                          <div className="flex items-start gap-2">
                            {expense.is_recurring && (
                              <RefreshCw className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                                expense.recurrence_active ? 'text-blue-500' : 'text-slate-300'
                              }`} />
                            )}
                            <div>
                              <p className="font-medium text-slate-900">{expense.name}</p>
                              {expense.supplier && (
                                <p className="text-xs text-slate-500">{expense.supplier}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getTypeInfo(expense.expense_type).color} border-0`}>
                            {getTypeInfo(expense.expense_type).label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {formatCurrency(expense.total_amount)}
                        </TableCell>
                        <TableCell className="text-emerald-600 font-medium">
                          {formatCurrency(expense.total_paid)}
                        </TableCell>
                        <TableCell className="text-red-600 font-medium">
                          {formatCurrency(expense.pending_balance)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-mono text-sm">{formatDateDisplay(expense.due_date)}</span>
                            {isOverdue && (
                              <span className="text-xs text-red-600 font-medium">
                                Vencido hace {Math.abs(daysUntilDue)} días
                              </span>
                            )}
                            {isDueSoon && (
                              <span className="text-xs text-yellow-600 font-medium">
                                Vence en {daysUntilDue} días
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusInfo(expense.payment_status).color} border-0`}>
                            {getStatusInfo(expense.payment_status).label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDetailModal(expense)}
                              className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {expense.pending_balance > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openPaymentModal(expense)}
                                className="h-8 w-8 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                                title="Registrar pago"
                              >
                                <CreditCard className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(expense)}
                              className="h-8 w-8 text-slate-500 hover:text-brand-main hover:bg-brand-main/10"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {expense.is_recurring && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleRecurrence(expense)}
                                className={`h-8 w-8 ${expense.recurrence_active 
                                  ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50' 
                                  : 'text-green-500 hover:text-green-600 hover:bg-green-50'
                                }`}
                                title={expense.recurrence_active ? 'Pausar recurrencia' : 'Activar recurrencia'}
                              >
                                {expense.recurrence_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setExpenseToDelete(expense); setDeleteDialogOpen(true); }}
                              className="h-8 w-8 text-slate-500 hover:text-red-500 hover:bg-red-50"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Sub-component for financial report
const FinancialReportContent = ({
  reportData, reportPeriod, setReportPeriod, reportLoading,
  exportReportToExcel, exportReportToPDF, formatCurrency
}) => {
  return (
    <div className="space-y-6">
      {/* Period selector */}
      <Card className="bg-white border-slate-100 shadow-soft">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Label className="text-slate-600">Período:</Label>
              <Select value={reportPeriod} onValueChange={setReportPeriod}>
                <SelectTrigger className="w-[200px] bg-slate-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">Este mes</SelectItem>
                  <SelectItem value="last_month">Mes anterior</SelectItem>
                  <SelectItem value="last_3_months">Últimos 3 meses</SelectItem>
                  <SelectItem value="last_6_months">Últimos 6 meses</SelectItem>
                  <SelectItem value="this_year">Este año</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  disabled={!reportData || reportLoading}
                >
                  <FileSpreadsheet className="w-5 h-5 mr-2" />
                  Exportar
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportReportToExcel} className="cursor-pointer">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                  Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportReportToPDF} className="cursor-pointer">
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {reportLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a5192]" />
        </div>
      ) : reportData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-600 text-sm font-medium">Ingresos</p>
                    <p className="text-3xl font-bold text-emerald-700 mt-1">
                      {formatCurrency(reportData.summary.total_income)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Gastos</p>
                    <p className="text-3xl font-bold text-red-700 mt-1">
                      {formatCurrency(reportData.summary.total_expenses)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${reportData.summary.balance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${reportData.summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'} text-sm font-medium`}>
                      Balance
                    </p>
                    <p className={`text-3xl font-bold mt-1 ${reportData.summary.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatCurrency(reportData.summary.balance)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${reportData.summary.balance >= 0 ? 'bg-emerald-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
                    <DollarSign className={`w-6 h-6 ${reportData.summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          {reportData.chart_data && reportData.chart_data.length > 0 && (
            <Card className="bg-white border-slate-100 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg text-slate-800">
                  Ingresos vs Gastos por Mes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={reportData.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `Q${(value/1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                      />
                      <Legend />
                      <Bar dataKey="ingresos" name="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="gastos" name="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      <Line 
                        type="monotone" 
                        dataKey="balance" 
                        name="Balance" 
                        stroke="#1a5192" 
                        strokeWidth={3}
                        dot={{ fill: '#1a5192', strokeWidth: 2 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Detail Table */}
          <Card className="bg-white border-slate-100 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-slate-800">
                Detalle Mensual
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Mes/Año</TableHead>
                      <TableHead className="font-semibold text-right">Ingresos</TableHead>
                      <TableHead className="font-semibold text-right">Gastos Fijos</TableHead>
                      <TableHead className="font-semibold text-right">Gastos Proveedores</TableHead>
                      <TableHead className="font-semibold text-right">Gastos Variables</TableHead>
                      <TableHead className="font-semibold text-right">Total Gastos</TableHead>
                      <TableHead className="font-semibold text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.monthly_data.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{row.month_name}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatCurrency(row.income)}</TableCell>
                        <TableCell className="text-right text-blue-600">{formatCurrency(row.expenses_fixed)}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(row.expenses_supplier)}</TableCell>
                        <TableCell className="text-right text-slate-600">{formatCurrency(row.expenses_variable)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(row.expenses_total)}</TableCell>
                        <TableCell className={`text-right font-bold ${row.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(row.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow className="bg-slate-100 font-bold">
                      <TableCell>TOTALES</TableCell>
                      <TableCell className="text-right text-emerald-700">{formatCurrency(reportData.summary.total_income)}</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right text-red-700">{formatCurrency(reportData.summary.total_expenses)}</TableCell>
                      <TableCell className={`text-right ${reportData.summary.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {formatCurrency(reportData.summary.balance)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-12 text-slate-400">
          Seleccione un período para ver el reporte
        </div>
      )}
    </div>
  );
};

export default Expenses;
